import { STATUS_LABEL } from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const STATUS_CLS = {
  not_started: "bg-stone-100 text-stone-400 border-stone-200",
  learning: "bg-amber-50 text-amber-700 border-amber-300",
  needs_practice: "bg-red-50 text-red-700 border-red-300",
  mastered: "bg-green-50 text-green-700 border-green-400",
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="scroll-card p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
    <div className="text-4xl font-bold text-[var(--tea)] mt-2">{value}<span className="text-lg text-stone-400 font-semibold">{sub}</span></div>
  </div>
);

const SentenceList = ({ title, rows, empty, testid, tone, metric }) => (
  <div className="scroll-card p-6" data-testid={testid}>
    <h2 className="text-lg font-bold mb-3">{title}</h2>
    {rows.length ? (
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.sentence_id} className={`flex items-center justify-between gap-3 rounded-xl px-4 py-2 border-2 ${tone}`}>
            <span className="font-round text-xl">{r.text}</span><span className="text-sm text-stone-500 shrink-0">{metric(r)}</span>
          </div>
        ))}
      </div>
    ) : <p className="text-sm text-stone-400">{empty}</p>}
  </div>
);

export const SpeakProgress = ({ s }) => (
  <div className="space-y-6" data-testid="speak-progress">
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
      <Stat testid="speak-stat-sentences" label="Sentences Practiced" value={s.sentences_practiced} sub={` / ${s.sentences_total}`} />
      <Stat testid="speak-stat-week" label="Speaking Practice This Week" value={s.week_speaking} sub=" times" />
      <Stat testid="speak-stat-recordings" label="Recordings Made" value={s.record_total} />
      <Stat testid="speak-stat-topics" label="Situations Completed" value={s.topics_completed} sub={` / ${s.topics_total}`} />
    </section>
    <section className="grid lg:grid-cols-2 gap-6">
      <SentenceList testid="speak-top" title="Most Practiced Sentences" rows={s.top} empty="No speaking practice yet" tone="border-green-200 bg-green-50/50"
        metric={(r) => `said ${r.speaks}× · recorded ${r.recordings}×`} />
      <SentenceList testid="speak-needs" title="Needs More Practice" rows={s.needs} empty="Nothing to review yet" tone="border-red-200 bg-red-50/50"
        metric={(r) => (r.accuracy == null ? `said ${r.speaks}×` : `${Math.round(r.accuracy * 100)}% · said ${r.speaks}×`)} />
    </section>
    <section className="scroll-card p-6" data-testid="speak-topic-progress">
      <h2 className="text-lg font-bold mb-4">Topics</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {s.topics.map((t) => (
          <div key={t.topic_id} className="flex items-center justify-between rounded-xl border px-4 py-2" data-testid={`speak-dash-topic-${t.topic_id}`}>
            <span className="font-round text-lg">{t.emoji} {t.title}</span>
            <span className="text-sm text-stone-500">{t.state === "completed" ? <span className="text-amber-500">{"★".repeat(t.stars)}</span> : t.state === "locked" ? "Locked" : "Open"} · {t.practiced}/{t.total}</span>
          </div>
        ))}
      </div>
    </section>
    <section className="scroll-card p-6 overflow-hidden" data-testid="speak-sentence-table">
      <h2 className="text-lg font-bold mb-3">Sentence Progress</h2>
      <div className="max-h-[420px] overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Sentence</TableHead><TableHead>Topic</TableHead><TableHead>Listened</TableHead><TableHead>Spoken</TableHead><TableHead>Recorded</TableHead><TableHead>Choice accuracy</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {s.sentences.map((r) => (
              <TableRow key={r.sentence_id}>
                <TableCell className="font-round text-lg">{r.text}</TableCell>
                <TableCell className="text-sm text-stone-500">{r.topic}</TableCell>
                <TableCell>{r.listens}</TableCell><TableCell>{r.speaks}</TableCell><TableCell>{r.recordings}</TableCell>
                <TableCell>{r.accuracy == null ? "—" : `${Math.round(r.accuracy * 100)}%`}</TableCell>
                <TableCell><span className={`px-2 py-1 rounded-full border text-xs ${STATUS_CLS[r.status]}`}>{STATUS_LABEL[r.status]}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  </div>
);
